import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { TFunction } from "i18next";
import TacticalPlayerNode, {
  type TacticalPlayerNodeHandle,
  type TeamColorStyle,
} from "./TacticalPlayerNode";
import { ColorSwatchButton } from "./TeamSelector";
import type { TeamColorInfo } from "./TeamSelector";
import type { TacticalPosition } from "../../hooks/useTacticalPositions";
import {
  getBoundsForPlayer,
  getPositionGroup,
  MIN_PLAYER_SEPARATION,
} from "../../hooks/useTacticalPositions";
import type { FieldAnchor, FieldCoordinate } from "../../types";
import { PITCH_HEIGHT, PITCH_WIDTH } from "../../utils/heatMapZones";
import {
  PENALTY_ARC_Y_OFFSET,
  PITCH_MARKINGS,
  PITCH_MARKINGS_DERIVED,
  toPitchY,
} from "../../utils/pitchGeometry";
import { formatDisplayName } from "../../../../lib/nameFormat";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Player {
  id: string;
  full_name: string;
  short_name?: string;
  jersey_number: number;
  position: string;
}

interface TacticalFieldProps {
  homeTeamName: string;
  awayTeamName: string;
  homeColorStyle?: TeamColorStyle;
  awayColorStyle?: TeamColorStyle;
  homePlayers: Player[];
  awayPlayers: Player[];
  expelledPlayerIds?: Set<string>;
  disciplinaryStatusByPlayer?: Record<
    string,
    { yellowCount: number; red: boolean }
  >;
  /** Canonical positions keyed by player‑id. */
  getDisplayPosition: (
    playerId: string,
    flipSides: boolean,
  ) => TacticalPosition;
  /** Called after the user finishes dragging a player. */
  onPlayerDragEnd: (
    playerId: string,
    displayX: number,
    displayY: number,
    playerPosition: string | undefined,
    side: "home" | "away",
  ) => void;
  /** Called when a player node is clicked (no drag). */
  onPlayerClick: (
    player: Player,
    anchor: FieldAnchor,
    location: [number, number],
    side: "home" | "away",
  ) => void;
  /** Optional destination click handler (for pass/shot destination). */
  onDestinationClick?: (coordinate: FieldCoordinate) => void;
  showDestinationControls?: boolean;
  /**
   * EPIC-96 (T4, client follow-up 2026-09-24): "is it really necessary to
   * have that blue dot... can we have a more well developed indicator in
   * the field?" — AC3 removed the interactive chip outright; this restores
   * the "developed indicator" half of the ask as a purely decorative guide:
   * a subtle tint + outline over the ATTACKED six-yard box (same box
   * `tactical-goalarea-left/right` already draw), rendered INSIDE the
   * `tactical-field-markings` SVG (which is already `pointer-events-none`
   * end to end) so it can never intercept a tap and always paints under
   * the `z-10` player-token layer by ordinary stacking, no z-index tuning
   * needed. `"left"` or `"right"` in the SAME fixed/canonical SVG space
   * `tactical-goalarea-left/right` already draw in (never flipped by
   * `flipSides`) — the caller converts the shooter's canonical attacked
   * edge to this DISPLAY-space edge, same convention the retired chip's
   * own `onTargetChipEdge` prop used. `undefined` renders no guide.
   */
  shotGuideEdge?: "left" | "right";
  overlay?: React.ReactNode;
  /**
   * PMA-COCK-185: true while `overlay` is the `GoalPlacementOverlay` (an
   * eligible Shot just confirmed) — makes the pitch itself inert: token
   * selection (`onPlayerClick`) and coordinate/destination taps
   * (`onDestinationClick`) all early-return, mouse and touch alike. Purely
   * defensive/explicit (the overlay's own scrim already sits on top and
   * would win any REAL browser hit-test), but required for a synthetic
   * `fireEvent`-driven test, which bypasses hit-testing/stacking entirely
   * and can target a token's DOM node directly.
   */
  overlayBlocksPitch?: boolean;
  flipSides?: boolean;
  /** Currently-dragged player id — used for bounds overlay + collision ghosts. */
  draggingPlayerId?: string | null;
  /** Setter so inner TacticalPlayerNode can propagate drag‑start/end. */
  onDragStart?: (playerId: string) => void;
  onDragStop?: () => void;
  /** All canonical positions — used for collision‑avoidance proximity rings. */
  allPositions?: Map<string, TacticalPosition>;
  /** When true (live match), expand drag bounds to the full field. */
  isMatchLive?: boolean;
  /** When set, only render player nodes whose id is in this set. */
  visiblePlayerIds?: Set<string>;
  /** Color picker — pass team color info to enable the chip swatch button. */
  homeTeamColorInfo?: TeamColorInfo;
  awayTeamColorInfo?: TeamColorInfo;
  /** Called after a successful color patch so parent can refresh team state. */
  onTeamColorSaved?: (
    teamId: string,
    primary: string | null,
    secondary: string | null,
  ) => void;
  /** i18n translation function — required to enable the color picker swatch in chips. */
  t?: TFunction<"logger">;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * See the "Width is controlled by TWO layers" comment at the field div below
 * for why this must stay a static string literal — a template interpolating
 * PITCH_WIDTH/PITCH_HEIGHT/the budget would defeat Tailwind's static-scan
 * class generation. 124/84 = (PITCH_WIDTH+4)/(PITCH_HEIGHT+4) with today's
 * constants (kept in sync by hand if those ever change).
 *
 * PMA-COCK-121 (2026-08-16, live-verified in a real Chromium browser via
 * Playwright, post-PMA-COCK-120 bench removal, at 1024x768 landscape — the
 * client's exact complaint viewport). This class alone is WIDTH-bound at
 * this viewport, not height-bound — CSS `min(100%, X)` can never exceed
 * `100%`, and `100%` here is fixed by the container chain (the rail-to-pitch
 * flex gap, `ActionStage.tsx`'s wrapper padding, `PlayerSelectorPanel.tsx`'s
 * own padding, and this file's own `px-2` wrapper below), not by this
 * class's `calc(...)` term. Measured stepwise: at the pre-PMA-COCK-121
 * padding chain the pitch was 698px wide and `calc(...)` only started
 * winning (shrinking the pitch) once budget exceeded ~295px (verified at
 * budget=100/280/400 -> 698/698/543px, not by arithmetic alone). Lead
 * approved reclaiming the horizontal dead width in that container chain
 * (client-approved design option, "dead width gone") in TWO rounds — round 1:
 * `gap-3` -> `gap-1` in `LoggerView.tsx`'s rail row, `p-3`->`p-1` in
 * `ActionStage.tsx`'s wrapper, `p-1.5`->`p-0.5` in `PlayerSelectorPanel.tsx`,
 * `px-2`->`px-0.5` below (698px -> 742px, +44px/+13% area); round 2:
 * `LoggerCockpit.tsx`'s page `<main>` horizontal padding, `px-4 sm:px-6` ->
 * `max-[1180px]:landscape:px-2` (742px -> **774px**, +32px more, +23% area
 * total vs. the pre-reclaim 698px baseline — no horizontal overflow,
 * confirmed via `document.documentElement.scrollWidth === clientWidth`).
 * All four+one changes are `max-[1180px]:landscape:`-scoped only; desktop
 * and portrait re-measured unchanged after every round, not just assumed
 * from the media-query semantics. This class's crossover moved down twice
 * alongside the ceiling — ~295px (pre-reclaim, 698px ceiling) -> ~265px
 * (round 1, 742px ceiling) -> ~244px (round 2, 774px ceiling), each step
 * re-verified empirically (e.g. round 2: budget=250 measurably shrinks the
 * pitch to ~765px against the 774px ceiling). `150` stays comfortably below
 * all three (94px of margin against the tightest) — never changed across
 * either round, since the reclaim happened entirely in the container chain.
 *
 * This class's own ceiling is still the container's `100%` at 1024x768 —
 * tuning the budget further down cannot grow the pitch past 774px; only
 * shrinking the container chain further (or moving off this exact
 * rail-width/breakpoint combination) would. See team_findings.md
 * (Phase 2 — fe-cockpit) for the full padding-chain breakdown, both
 * reclaim rounds, and the two rejected paths (accepting no growth; naively
 * setting budget from the measured gap-above, which would have shrunk the
 * pitch to ~353px).
 */
const PITCH_VIEWPORT_CAP_CLASS =
  "max-[1180px]:w-[min(100%,calc((100vh-150px)*124/84))] max-[819px]:portrait:w-[min(100%,calc((100vh-680px)*124/84))]";

/**
 * PMA-COCK-153: pixel radius used ONLY to decide, when the pointer's raw
 * nearest token is `isExpelled`, whether a genuinely-closer active
 * alternative exists nearby (crowded cluster — fall through to it) versus
 * the expelled token being the sole, isolated thing under the pointer
 * (nothing else is remotely close — absorb the tap, matching this app's
 * pre-existing behaviour for a tap on an inert token). NOT used to bound
 * whether the resolver engages at all — that bound comes for free from
 * `handleFieldPointerDownCapture`/`handleFieldPointerMove` only ever
 * running when the browser's OWN native hit-test already landed on some
 * `[data-tactical-player]` node (see those handlers). Sized generously
 * against the jersey circle's own rendered radius (`clamp(2.2rem, ...,
 * 3.2rem)` diameter → up to ~25.6px radius at a 16px root) plus the name
 * label/position badge stacked below it in the same hit box — comfortably
 * covers "another token is part of this same visual cluster" without
 * reaching across genuinely empty pitch.
 */
const TOKEN_HIT_RADIUS_PX = 60;

/**
 * EPIC-96 (T4): the tap-feedback ripple's total lifetime — brief, per the
 * client follow-up's own "fades in under ~400 ms" wording. CSS handles the
 * actual fade (a short opacity/scale transition on mount); this timer is
 * only what unmounts the element afterward, comfortably past the CSS
 * transition's own duration so the fade-out is never cut short.
 */
const TAP_RIPPLE_DURATION_MS = 500;

const resolveStatsbombLocation = (
  xPercent: number,
  yPercent: number,
): [number, number] => [
  Number(((xPercent / 100) * 120).toFixed(1)),
  Number(((yPercent / 100) * 80).toFixed(1)),
];

/**
 * PMA-COCK-174: goal-mouth zone thresholds for `buildCoordinate`'s
 * `goalMouth` field, documented in full in cockpit-contract.md.
 *
 * X-axis (distance from either goal line, in the same 0-100 `xPercent`
 * space `buildCoordinate` already works in): the brief calls for "inside
 * the goal line by up to `PITCH_MARKINGS.goalAreaDepth / 2`, or beyond it
 * by up to 4% (the existing out-of-bounds margin)". `GOAL_MOUTH_X_INSET_PCT`
 * is that half-depth converted from pitch units (`PITCH_WIDTH`-scale) to a
 * percentage of `PITCH_WIDTH`; `left = 4`/`right = 96` below are the exact
 * out-of-bounds constants already used a few lines down for
 * `isOutOfBounds`/`outOfBoundsEdge`, so the "beyond it" allowance is
 * whatever those already treat as in-bounds-adjacent, not a new number.
 *
 * Y-axis (goal-mouth span, centred on the pitch's own vertical centre,
 * matching `PITCH_MARKINGS_DERIVED.goalAreaY`'s own symmetric derivation):
 * the real goal (7.32m wide) sits centred inside the 18.32m-wide goal AREA
 * already drawn (`tactical-goalarea-left/right`) — and 18.32 = 7.32 + 2×5.5,
 * i.e. the goal area's own margin on each side of the goal frame is
 * exactly the SAME real-world 5.5m already encoded as
 * `PITCH_MARKINGS.goalAreaDepth` (just projected onto the goal LINE's axis
 * via `toPitchY` instead of the goal area's depth axis via `toPitchX` —
 * same distance, different scale). `GOAL_MOUTH_HEIGHT`/`GOAL_MOUTH_Y_PCT`
 * below derive the real goal frame this way rather than reusing the full
 * (much wider) goal-area box, so the highlighted zone reads as an actual
 * goal mouth, not the whole 6-yard box.
 */
// The out-of-bounds margin `buildCoordinate` already applies (`left =
// OOB_MARGIN_PCT`, `right = 100 - OOB_MARGIN_PCT`, same for top/bottom) —
// hoisted so `resolveGoalMouth`'s "beyond the goal line by up to 4%"
// allowance is provably the SAME number, not a re-typed duplicate that could
// drift from it.
const OOB_MARGIN_PCT = 4;
const GOAL_MOUTH_X_INSET_PCT =
  (PITCH_MARKINGS.goalAreaDepth / 2 / PITCH_WIDTH) * 100;
const GOAL_MOUTH_HEIGHT = PITCH_MARKINGS.goalAreaHeight - 2 * toPitchY(5.5);
const GOAL_MOUTH_Y = (PITCH_HEIGHT - GOAL_MOUTH_HEIGHT) / 2;
const GOAL_MOUTH_Y_MIN_PCT = (GOAL_MOUTH_Y / PITCH_HEIGHT) * 100;
const GOAL_MOUTH_Y_MAX_PCT =
  ((GOAL_MOUTH_Y + GOAL_MOUTH_HEIGHT) / PITCH_HEIGHT) * 100;
/** Centre of the goal-mouth y-band — the on-target chip's un-offset y. */
const GOAL_MOUTH_Y_CENTER_PCT =
  (GOAL_MOUTH_Y_MIN_PCT + GOAL_MOUTH_Y_MAX_PCT) / 2;
/**
 * PMA-COCK-181 (client ask 2026-09-10): the on-target chip's own centre,
 * un-offset, sits at the midpoint of the SAME "inside the pitch" goal-mouth
 * portion the old highlight rect covered (x in pitch-percent from the goal
 * line to `GOAL_MOUTH_X_INSET_PCT` — i.e. half that inset from the line).
 *
 * EPIC-96 (AC3, client wave 2026-09-24): the interactive on-pitch chip this
 * constant used to position is retired — it sat `z-20 pointer-events-auto`
 * over the player-token layer, which is exactly what let it intercept a tap
 * meant for the goalkeeper ("me estorba al querer seleccionar al portero").
 * The action moved off the pitch entirely, to a labelled button in
 * `ActionStage.tsx`'s own hint bar — this constant (and
 * `buildOnTargetChipCoordinate` below) now only supplies that button's
 * dispatched `end_location`, never anything rendered on the pitch, so the
 * keeper-avoid slide this file used to compute for the chip's own on-screen
 * position no longer has a token to avoid and was removed outright (see the
 * retired `ON_TARGET_CHIP_AVOID_RADIUS_PCT`/`ON_TARGET_CHIP_Y_MARGIN_PCT`
 * pair's git history for the multi-pass resolver this simplification
 * deletes).
 */
const ON_TARGET_CHIP_X_INSET_PCT = GOAL_MOUTH_X_INSET_PCT / 2;

const resolveGoalMouth = (
  flippedX: number,
  yPercent: number,
): FieldCoordinate["goalMouth"] => {
  if (yPercent < GOAL_MOUTH_Y_MIN_PCT || yPercent > GOAL_MOUTH_Y_MAX_PCT) {
    return null;
  }
  if (flippedX >= -OOB_MARGIN_PCT && flippedX <= GOAL_MOUTH_X_INSET_PCT) {
    return "left";
  }
  if (
    flippedX <= 100 + OOB_MARGIN_PCT &&
    flippedX >= 100 - GOAL_MOUTH_X_INSET_PCT
  ) {
    return "right";
  }
  return null;
};

/**
 * PMA-COCK-146: a player-node click passes `display.x/y` — the position
 * `getDisplayPosition` already transformed FOR RENDERING by the operator's
 * manual "flip my view" toggle (`flipSides`/`manualFieldFlip`,
 * `useTacticalPositions.ts`'s `getDisplayPosition`: `{x: 100 - canonical.x,
 * y: canonical.y}` when flipped). That toggle is a pure personal
 * screen-orientation preference (`CockpitContext.tsx`'s `manualFieldFlip`,
 * `useState(false)`) with no tie to `period`/half or real attacking
 * direction — confirmed no cross-reference to `currentPhase`/`period`
 * anywhere it's read or set. `buildCoordinate` below already un-flips a
 * DESTINATION click's raw percentage back to canonical space before
 * converting to StatsBomb units, so a click at the same physical spot always
 * yields the same coordinate regardless of the toggle. The player-click path
 * skipped that un-flip and fed the already-mirrored display value straight
 * into `resolveStatsbombLocation` — so toggling the view (zero football
 * meaning) silently changed a logged Shot's exported x. This helper mirrors
 * `buildCoordinate`'s own un-flip so the two paths agree.
 */
const resolveCanonicalStatsbombLocation = (
  displayXPercent: number,
  displayYPercent: number,
  flipSides: boolean,
): [number, number] => {
  // EPIC-97: "Voltear cancha" is a 180-degree rotation, not a left-right
  // mirror — `getDisplayPosition` (useTacticalPositions.ts) now transforms
  // BOTH axes for a flipped token, so the un-flip here must invert both too.
  const canonicalXPercent = flipSides ? 100 - displayXPercent : displayXPercent;
  const canonicalYPercent = flipSides ? 100 - displayYPercent : displayYPercent;
  return resolveStatsbombLocation(canonicalXPercent, canonicalYPercent);
};

const buildCoordinate = (
  xPercent: number,
  yPercent: number,
  flipSides: boolean,
): FieldCoordinate => {
  // EPIC-97: both axes invert under the 180-degree rotation — see the
  // matching comment on `resolveCanonicalStatsbombLocation` above.
  const flippedX = flipSides ? 100 - xPercent : xPercent;
  const flippedY = flipSides ? 100 - yPercent : yPercent;
  const left = OOB_MARGIN_PCT;
  const right = 100 - OOB_MARGIN_PCT;
  const top = OOB_MARGIN_PCT;
  const bottom = 100 - OOB_MARGIN_PCT;
  let outOfBoundsEdge: FieldCoordinate["outOfBoundsEdge"] = null;
  const isOutOfBounds =
    flippedX < left || flippedX > right || flippedY < top || flippedY > bottom;
  if (flippedX < left) outOfBoundsEdge = "left";
  else if (flippedX > right) outOfBoundsEdge = "right";
  else if (flippedY < top) outOfBoundsEdge = "top";
  else if (flippedY > bottom) outOfBoundsEdge = "bottom";
  const cx = Math.min(100, Math.max(0, flippedX));
  const cy = Math.min(100, Math.max(0, flippedY));
  return {
    xPercent: cx,
    yPercent: cy,
    statsbomb: resolveStatsbombLocation(cx, cy),
    isOutOfBounds,
    outOfBoundsEdge,
    goalMouth: resolveGoalMouth(flippedX, flippedY),
  };
};

/**
 * EPIC-96 (AC3, client wave 2026-09-24): the fixed "on target, no credit"
 * coordinate — the same un-offset midpoint of the goal-mouth zone at the
 * given DISPLAY-space edge (`ActionStage.tsx` converts the shooter's
 * canonical attacked edge before calling this, the same conversion it
 * already did for the retired chip's own `onTargetChipEdge` prop) the
 * PMA-COCK-181 on-target chip used to sit at before it was retired from the
 * pitch. `ActionStage.tsx` calls this directly from its own off-pitch
 * hint-bar button (`field-ontarget-btn`) and passes the result straight to
 * `handleFieldDestination`, tagged `onTargetChip: true` so
 * `useActionFlow.ts`'s `handleDestinationClick` short-circuits to the same
 * no-credit `OnTarget` dispatch a chip tap used to produce — no rendering,
 * no token-avoidance needed any more since nothing is drawn on the pitch at
 * this coordinate. Exported (not a component-internal `useCallback`) since
 * the caller no longer holds a `TacticalField` instance to call a prop on.
 */
export const buildOnTargetChipCoordinate = (
  edge: "left" | "right",
  flipSides: boolean,
): FieldCoordinate => {
  const baseX =
    edge === "left"
      ? ON_TARGET_CHIP_X_INSET_PCT
      : 100 - ON_TARGET_CHIP_X_INSET_PCT;
  const baseY = GOAL_MOUTH_Y_CENTER_PCT;
  return { ...buildCoordinate(baseX, baseY, flipSides), onTargetChip: true };
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const TacticalField: React.FC<TacticalFieldProps> = ({
  homeTeamName,
  awayTeamName,
  homeColorStyle,
  awayColorStyle,
  homePlayers,
  awayPlayers,
  expelledPlayerIds,
  disciplinaryStatusByPlayer,
  getDisplayPosition,
  onPlayerDragEnd,
  onPlayerClick,
  onDestinationClick,
  showDestinationControls = false,
  shotGuideEdge,
  overlay,
  overlayBlocksPitch = false,
  flipSides = false,
  visiblePlayerIds,
  draggingPlayerId = null,
  onDragStart,
  onDragStop,
  allPositions,
  isMatchLive = false,
  homeTeamColorInfo,
  awayTeamColorInfo,
  onTeamColorSaved,
  t,
}) => {
  const resolvedHomeStyle =
    homeColorStyle ??
    ({
      bg: "#dc2626",
      border: "rgba(255,200,200,0.8)",
      labelBg: "rgba(100,20,20,0.92)",
      text: "#ffffff",
    } as TeamColorStyle);
  const resolvedAwayStyle =
    awayColorStyle ??
    ({
      bg: "#1e40af",
      border: "rgba(180,220,255,0.8)",
      labelBg: "rgba(20,40,100,0.92)",
      text: "#ffffff",
    } as TeamColorStyle);

  const FIELD_LINE_STROKE_WIDTH = 0.4;
  const FIELD_BOUNDARY_INSET = FIELD_LINE_STROKE_WIDTH / 2;
  const FIELD_BOUNDARY_WIDTH = PITCH_WIDTH - FIELD_LINE_STROKE_WIDTH;
  const FIELD_BOUNDARY_HEIGHT = PITCH_HEIGHT - FIELD_LINE_STROKE_WIDTH;
  const FIELD_BOTTOM_LINE_Y = PITCH_HEIGHT - FIELD_BOUNDARY_INSET;

  const PA_WIDTH = PITCH_MARKINGS.penaltyAreaDepth;
  const PA_HEIGHT = PITCH_MARKINGS.penaltyAreaHeight;
  const PA_Y = PITCH_MARKINGS_DERIVED.penaltyAreaY;
  const GA_WIDTH = PITCH_MARKINGS.goalAreaDepth;
  const GA_HEIGHT = PITCH_MARKINGS.goalAreaHeight;
  const GA_Y = PITCH_MARKINGS_DERIVED.goalAreaY;
  const CC_RX = PITCH_MARKINGS.centerCircleRx;
  const CC_RY = PITCH_MARKINGS.centerCircleRy;
  const PEN_X = PITCH_MARKINGS.penaltyMarkX;
  const PEN_R = PITCH_MARKINGS.penaltyMarkRadius;
  const CORNER_R = PITCH_MARKINGS.cornerArcR;

  // Inner-pitch inset percentages — match the SVG viewBox's 2-unit margin.
  // Container is (PITCH_WIDTH+4) × (PITCH_HEIGHT+4); the pitch boundary starts
  // 2 units in from each edge. These four values map 0..100% in the pitch region
  // exactly onto the visible pitch markings, so player chips align inside the lines.
  const PITCH_LEFT_PCT = (2 / (PITCH_WIDTH + 4)) * 100;
  const PITCH_TOP_PCT = (2 / (PITCH_HEIGHT + 4)) * 100;
  const PITCH_W_PCT = (PITCH_WIDTH / (PITCH_WIDTH + 4)) * 100;
  const PITCH_H_PCT = (PITCH_HEIGHT / (PITCH_HEIGHT + 4)) * 100;

  const fieldRef = useRef<HTMLDivElement | null>(null);
  /** Ref on the inner-pitch overlay — used for click-to-place coordinate conversion. */
  const pitchRef = useRef<HTMLDivElement | null>(null);
  const lastTouchDestinationAt = useRef(0);

  // Transient drag preview — while a player is mid‑drag we store their
  // temporary position here so the node re‑renders at the new spot without
  // committing to the canonical store yet.
  const [dragPreview, setDragPreview] = useState<{
    id: string;
    x: number;
    y: number;
  } | null>(null);

  /**
   * EPIC-96 (T4): brief, non-interactive tap-feedback ripple — the second
   * half of the "developed indicator" ask, so the operator sees exactly
   * where an open-pitch tap landed. Only armed while `shotGuideEdge` is
   * set (the same shot/"Gol"-origin destination steps the pitch guide
   * above is scoped to) — `submitDestinationAtPoint` is the one shared
   * choke point both mouse click and touch tap already funnel through for
   * an OPEN-PITCH coordinate tap. A tap that lands ON a player token
   * doesn't go through this path at all (the token itself, already
   * visibly rendered, IS the feedback there), so this deliberately never
   * fires for one. `rippleId` is a monotonic counter (not just a
   * boolean/timestamp) so two ripples in quick succession at the exact
   * same point still each restart their own fade — React wouldn't
   * otherwise see the state as "changed" from an identical {x, y} object.
   */
  const [tapRipple, setTapRipple] = useState<{
    x: number;
    y: number;
    rippleId: number;
  } | null>(null);
  const rippleIdRef = useRef(0);
  const rippleTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    return () => {
      if (rippleTimeoutRef.current) clearTimeout(rippleTimeoutRef.current);
    };
  }, []);

  // ─── PMA-COCK-153: pitch-level "closest token wins" hit-testing ─────────
  //
  // Root cause this closes: once two tokens' rendered boxes overlap (any
  // crowding level — collision-avoidance layout fixes only make it rarer,
  // never impossible, see PMA-COCK-153), the BROWSER resolves hover/click to
  // whichever node is topmost in DOM stacking order, independent of which
  // one is actually painted under the cursor. `document.elementFromPoint()`
  // proved this live against a token's own visible label. The fix: redirect
  // pointerdown (and drive the hover tooltip) through distance-to-token-
  // centre math computed here, instead of trusting the native hit-test's
  // choice of DOM target.
  //
  // Each TacticalPlayerNode keeps its OWN complete gesture state machine
  // (drag threshold, pointerId isolation — PMA-COCK-152, cancel/unmount
  // cleanup — PMA-COCK-150) entirely unchanged; this only decides WHICH
  // node's `handlePointerDown` actually runs for a given pointerdown, via
  // the imperative ref below, instead of letting raw DOM stacking order
  // decide. See TacticalPlayerNode.tsx's `TacticalPlayerNodeHandle` comment
  // for the other half.
  const nodeRefs = useRef<Map<string, TacticalPlayerNodeHandle>>(new Map());
  const [hoverPlayerId, setHoverPlayerId] = useState<string | null>(null);

  /** Every player currently rendered on the pitch (same filter renderPlayerNode
   *  applies), each tagged with its side — the candidate pool for hit-testing. */
  const renderedPlayers = useMemo(() => {
    const visible = (p: Player) =>
      !visiblePlayerIds || visiblePlayerIds.has(p.id);
    return [
      ...homePlayers
        .filter(visible)
        .map((p) => ({ ...p, side: "home" as const })),
      ...awayPlayers
        .filter(visible)
        .map((p) => ({ ...p, side: "away" as const })),
    ];
  }, [homePlayers, awayPlayers, visiblePlayerIds]);

  /** A player's CURRENT display position — dragPreview-aware, matching
   *  exactly what renderPlayerNode feeds each node's own xPercent/yPercent. */
  const getTokenDisplay = useCallback(
    (playerId: string): TacticalPosition =>
      dragPreview?.id === playerId
        ? { x: dragPreview.x, y: dragPreview.y }
        : getDisplayPosition(playerId, flipSides),
    [dragPreview, getDisplayPosition, flipSides],
  );

  /**
   * Resolve the token whose rendered CENTRE is physically closest to a
   * page-pixel point — the "closest token wins" rule itself. Prefers a
   * non-expelled candidate over a closer expelled one, but only when that
   * alternative is within `TOKEN_HIT_RADIUS_PX` (a genuinely-crowded
   * neighbour), so an isolated tap on an expelled token (nothing else
   * remotely close) still resolves to that expelled token — matching the
   * pre-existing "tap on an inert token absorbs, does nothing" behaviour
   * rather than jumping to an unrelated distant active player.
   */
  const resolveNearestPlayerId = useCallback(
    (clientX: number, clientY: number): string | null => {
      const rect = pitchRef.current?.getBoundingClientRect();
      if (!rect || renderedPlayers.length === 0) return null;
      const scored = renderedPlayers
        .map((p) => {
          const display = getTokenDisplay(p.id);
          const px = rect.left + (display.x / 100) * rect.width;
          const py = rect.top + (display.y / 100) * rect.height;
          return {
            id: p.id,
            isExpelled: expelledPlayerIds?.has(p.id) ?? false,
            dist: Math.hypot(clientX - px, clientY - py),
          };
        })
        .sort((a, b) => a.dist - b.dist);
      const nearest = scored[0];
      if (!nearest) return null;
      if (!nearest.isExpelled) return nearest.id;
      const closeActiveAlternative = scored.find(
        (c) => !c.isExpelled && c.dist <= TOKEN_HIT_RADIUS_PX,
      );
      return (closeActiveAlternative ?? nearest).id;
    },
    [renderedPlayers, getTokenDisplay, expelledPlayerIds],
  );

  /**
   * Capture phase — runs BEFORE any token's own bubble-phase `onPointerDown`
   * (capture always precedes the target's own listeners, even one that
   * calls `stopPropagation()` on itself, which every node's own handler
   * does). Only ever fires when the native hit-test landed on SOME token in
   * the first place (`.closest('[data-tactical-player]')`) — a pointerdown
   * on empty grass never reaches here, so destination-click placement is
   * completely unaffected. When the natively-hit node IS the nearest one
   * (the common, non-crowded case), this is a no-op — zero behaviour
   * change. Only when they disagree does it stop the wrong node's own
   * handler from ever running and redirect to the correct one via its
   * imperative `handlePointerDown` ref instead.
   */
  const handleFieldPointerDownCapture = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      // PMA-COCK-185: the overlay's own scrim already wins any real
      // hit-test (see the prop doc comment); this early return only
      // matters for a synthetic event dispatched straight at a token.
      if (overlayBlocksPitch) return;
      const targetEl = e.target as HTMLElement | null;
      const hitEl = targetEl?.closest?.(
        "[data-tactical-player]",
      ) as HTMLElement | null;
      if (!hitEl) return;
      const hitPlayerId = hitEl.getAttribute("data-tactical-player");
      const nearestId = resolveNearestPlayerId(e.clientX, e.clientY);
      if (!nearestId || nearestId === hitPlayerId) return;
      const handle = nodeRefs.current.get(nearestId);
      if (!handle) return;
      e.preventDefault();
      e.stopPropagation();
      handle.handlePointerDown(e);
    },
    [overlayBlocksPitch, resolveNearestPlayerId],
  );

  /**
   * Hover — same resolver, same "only when native hit-test found some
   * token" bound, so the wrong-name tooltip bug (the client's actual
   * report) is closed the same way the click/drag redirect closes it.
   * Mouse-only: touch has no persistent hover state, so no tooltip is shown
   * for `pointerType !== "mouse"` (matches how the removed native `title`
   * behaved in practice on touch devices).
   */
  const handleFieldPointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.pointerType !== "mouse") return;
      const targetEl = e.target as HTMLElement | null;
      const hitEl = targetEl?.closest?.(
        "[data-tactical-player]",
      ) as HTMLElement | null;
      if (!hitEl) {
        setHoverPlayerId((prev) => (prev === null ? prev : null));
        return;
      }
      const hitPlayerId = hitEl.getAttribute("data-tactical-player");
      const nearestId =
        resolveNearestPlayerId(e.clientX, e.clientY) ?? hitPlayerId;
      setHoverPlayerId((prev) => (prev === nearestId ? prev : nearestId));
    },
    [resolveNearestPlayerId],
  );

  const handleFieldPointerLeave = useCallback(() => {
    setHoverPlayerId(null);
  }, []);

  const submitDestinationAtPoint = useCallback(
    (clientX: number, clientY: number) => {
      if (!onDestinationClick || !pitchRef.current) return;
      const rect = pitchRef.current.getBoundingClientRect();
      const xPercent = ((clientX - rect.left) / rect.width) * 100;
      const yPercent = ((clientY - rect.top) / rect.height) * 100;
      if (shotGuideEdge) {
        rippleIdRef.current += 1;
        setTapRipple({
          x: xPercent,
          y: yPercent,
          rippleId: rippleIdRef.current,
        });
        if (rippleTimeoutRef.current) clearTimeout(rippleTimeoutRef.current);
        rippleTimeoutRef.current = setTimeout(() => {
          setTapRipple(null);
        }, TAP_RIPPLE_DURATION_MS);
      }
      onDestinationClick(buildCoordinate(xPercent, yPercent, flipSides));
    },
    [onDestinationClick, flipSides, shotGuideEdge],
  );

  // EPIC-96 (AC3): the on-pitch chip that used to call this (and the
  // keeper-avoid resolver that positioned it) is retired — see
  // `buildOnTargetChipCoordinate` below, a module-level pure function
  // `ActionStage.tsx` now calls directly to dispatch the same "on target,
  // no credit" Shot from its own off-pitch hint-bar button.

  const handleFieldClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      // PMA-COCK-185: no coordinate destination while the placement overlay
      // is open (see the `overlayBlocksPitch` prop doc comment).
      if (overlayBlocksPitch) return;
      // Touch taps can emit both pointer and click; suppress duplicate click.
      if (Date.now() - lastTouchDestinationAt.current < 700) {
        return;
      }
      submitDestinationAtPoint(event.clientX, event.clientY);
    },
    [overlayBlocksPitch, submitDestinationAtPoint],
  );

  const handleFieldPointerUp = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (overlayBlocksPitch) return;
      if (event.pointerType !== "touch") return;
      // PMA-COCK-178: a touch pointerup whose target is a player token must
      // NOT submit a coordinate destination. TacticalPlayerNode delivers a
      // tap via its OWN window-level `pointerup` listener
      // (`handleWindowPointerUp`, registered in `handlePointerDown`), which
      // fires on `window` — below the React root in the bubble order. React
      // dispatches this field's synthetic `onPointerUp` from the root
      // container, i.e. BEFORE that window listener runs, so without this
      // guard a touch tap that lands on a token (a shot's destination step
      // included) always submitted a plain coordinate destination first —
      // OnTarget with no target player — and only THEN ran the token's tap,
      // by which point no action was pending, so the keeper/blocker credit
      // (`saved_by_player_id`/`blocked_by_player_id`) never reached the
      // event. Mouse is unaffected: this handler already returns above for
      // `pointerType !== "touch"`, and the token's own `onClick` stops the
      // native click from ever reaching `handleFieldClick` there. See
      // team_findings.md 2026-09-09 for the full ordering trace and
      // PMA-COCK-153 (the capture-phase nearest-token redirect, untouched
      // by this guard — it only concerns `pointerdown`).
      const targetEl = event.target as HTMLElement | null;
      if (targetEl?.closest?.("[data-tactical-player]")) return;
      lastTouchDestinationAt.current = Date.now();
      submitDestinationAtPoint(event.clientX, event.clientY);
    },
    [overlayBlocksPitch, submitDestinationAtPoint],
  );

  const makePlayerDragEnd = useCallback(
    (playerId: string, playerPosition: string, side: "home" | "away") =>
      (displayX: number, displayY: number) => {
        setDragPreview(null);
        onDragStop?.();
        onPlayerDragEnd(playerId, displayX, displayY, playerPosition, side);
      },
    [onPlayerDragEnd, onDragStop],
  );

  const makePlayerDragMove = useCallback(
    (playerId: string, _playerPosition: string, _side: "home" | "away") =>
      (displayX: number, displayY: number) => {
        setDragPreview({ id: playerId, x: displayX, y: displayY });
        onDragStart?.(playerId);
      },
    [onDragStart],
  );

  /**
   * PMA-COCK-150: a gesture that ends WITHOUT a commit — the browser firing
   * `pointercancel` mid-drag, or the dragged node unmounting mid-drag (both
   * handled inside TacticalPlayerNode, which calls this via its new
   * `onDragCancel` prop). Mirrors makePlayerDragEnd's cleanup exactly minus
   * the `onPlayerDragEnd` position commit, so a cancelled gesture still
   * never writes a position (unchanged, deliberate) but no longer leaves
   * `draggingPlayerId`/`dragPreview` stuck forever — the bug that rendered
   * the collision-proximity rings for every other player permanently.
   * Not parameterized by playerId: only one player can be mid-drag at a
   * time (the single shared `draggingPlayerId`), so an unconditional clear
   * is safe for whichever node's gesture actually cancelled.
   */
  const handleDragCancel = useCallback(() => {
    setDragPreview(null);
    onDragStop?.();
  }, [onDragStop]);

  const renderPlayerNode = useCallback(
    (player: Player, side: "home" | "away") => {
      const display = getTokenDisplay(player.id);
      const cardStatus = disciplinaryStatusByPlayer?.[player.id];
      const isExpelled = expelledPlayerIds?.has(player.id) ?? false;

      return (
        <TacticalPlayerNode
          key={player.id}
          ref={(instance) => {
            if (instance) nodeRefs.current.set(player.id, instance);
            else nodeRefs.current.delete(player.id);
          }}
          playerId={player.id}
          jerseyNumber={player.jersey_number}
          fullName={player.full_name}
          shortName={player.short_name}
          position={player.position}
          side={side}
          xPercent={display.x}
          yPercent={display.y}
          isExpelled={isExpelled}
          hasYellow={(cardStatus?.yellowCount ?? 0) > 0}
          hasRed={Boolean(cardStatus?.red)}
          yellowCount={cardStatus?.yellowCount ?? 0}
          colorStyle={side === "home" ? resolvedHomeStyle : resolvedAwayStyle}
          t={t}
          onClick={() => {
            // PMA-COCK-185: no player selection while the placement overlay
            // is open.
            if (overlayBlocksPitch) return;
            if (isExpelled) return;
            const anchor: FieldAnchor = {
              xPercent: display.x,
              yPercent: display.y,
            };
            const location = resolveCanonicalStatsbombLocation(
              display.x,
              display.y,
              flipSides,
            );
            onPlayerClick(player, anchor, location, side);
          }}
          onDragMove={makePlayerDragMove(player.id, player.position, side)}
          onDragEnd={makePlayerDragEnd(player.id, player.position, side)}
          onDragCancel={handleDragCancel}
        />
      );
    },
    [
      getTokenDisplay,
      flipSides,
      disciplinaryStatusByPlayer,
      expelledPlayerIds,
      onPlayerClick,
      overlayBlocksPitch,
      makePlayerDragMove,
      makePlayerDragEnd,
      handleDragCancel,
    ],
  );

  return (
    // max-[1180px]: reclaims a little of the team-label-chip row's own
    // vertical padding on tablet/phone (client's iPad "fits one screen" ask,
    // team_findings.md 2026-08-14), same 1180px threshold as
    // PITCH_VIEWPORT_CAP_CLASS above. Desktop (p-2) untouched.
    // PMA-COCK-121 (2026-08-16): px-2 -> max-[1180px]:landscape:px-0.5
    // reclaims a further 12px of horizontal dead width (6px/side) at iPad
    // landscape, part of the pitch-enlargement reclaim (client ask: "make
    // bigger the soccer field"). Desktop/portrait unaffected.
    <div className="relative w-full px-2 max-[1180px]:landscape:px-0.5 py-2 max-[1180px]:py-1 max-[820px]:portrait:py-0">
      <div className="relative">
        {/* ─── Team label chips — rendered ABOVE the pitch (not overlapping the field) ─── */}
        {(() => {
          const leftStyle = {
            backgroundColor: flipSides
              ? resolvedAwayStyle.labelBg
              : resolvedHomeStyle.labelBg,
            color: flipSides ? resolvedAwayStyle.text : resolvedHomeStyle.text,
            borderColor: flipSides
              ? resolvedAwayStyle.border
              : resolvedHomeStyle.border,
          };
          const rightStyle = {
            backgroundColor: flipSides
              ? resolvedHomeStyle.labelBg
              : resolvedAwayStyle.labelBg,
            color: flipSides ? resolvedHomeStyle.text : resolvedAwayStyle.text,
            borderColor: flipSides
              ? resolvedHomeStyle.border
              : resolvedAwayStyle.border,
          };
          const leftColorInfo = flipSides
            ? awayTeamColorInfo
            : homeTeamColorInfo;
          const rightColorInfo = flipSides
            ? homeTeamColorInfo
            : awayTeamColorInfo;
          return (
            <div className="flex justify-between items-stretch mb-1 gap-2">
              {/* Left chip — home side (or away when flipped) */}
              <div
                className="flex items-center gap-1 font-bold text-sm px-3 rounded-lg border backdrop-blur-sm shadow-sm"
                style={leftStyle}
              >
                <div className="py-1 max-[1180px]:py-0.5">
                  <span className="block">
                    {flipSides ? awayTeamName : homeTeamName}
                  </span>
                  <span className="text-xs text-white/70 font-normal">
                    {flipSides ? "Away" : "Home"}
                  </span>
                </div>
                {leftColorInfo && onTeamColorSaved && t && (
                  <ColorSwatchButton
                    colorInfo={leftColorInfo}
                    label={flipSides ? awayTeamName : homeTeamName}
                    onSaved={(primary, secondary) =>
                      onTeamColorSaved(leftColorInfo.teamId, primary, secondary)
                    }
                    t={t}
                  />
                )}
              </div>
              {/* Right chip — away side (or home when flipped) */}
              <div
                className="flex items-center gap-1 font-bold text-sm px-3 rounded-lg border backdrop-blur-sm shadow-sm"
                style={rightStyle}
              >
                {rightColorInfo && onTeamColorSaved && t && (
                  <ColorSwatchButton
                    colorInfo={rightColorInfo}
                    label={flipSides ? homeTeamName : awayTeamName}
                    onSaved={(primary, secondary) =>
                      onTeamColorSaved(
                        rightColorInfo.teamId,
                        primary,
                        secondary,
                      )
                    }
                    t={t}
                  />
                )}
                <div className="py-1 max-[1180px]:py-0.5">
                  <span className="block">
                    {flipSides ? homeTeamName : awayTeamName}
                  </span>
                  <span className="text-xs text-white/70 font-normal">
                    {flipSides ? "Home" : "Away"}
                  </span>
                </div>
              </div>
            </div>
          );
        })()}

        {/* ─── The soccer field ─── */}
        {/*
          Full-width responsive field, viewport-height-capped ONLY on
          short/wide viewports (<=1180px wide — phone and tablet landscape;
          desktop is never affected, see below).
          Aspect ratio = (PITCH_WIDTH+4) / (PITCH_HEIGHT+4) = 124/84, matching
          SoccerFieldHeatMap so the two views look identical. The 2-unit margin
          in the SVG viewBox (-2 -2 124 84) ensures pitch boundary lines are not
          flush with the container edge. overflow-visible (not overflow-hidden)
          allows player chip labels to extend beyond the container edge on small
          screens without being clipped.

          Width is controlled by TWO layers, kept deliberately separate:
            1. `w-full` (this className) — the always-on default. Wins on tall/
               portrait viewports and on any viewport wider than 1180px (i.e.
               every desktop size) since the responsive override below never
               fires there.
            2. `PITCH_VIEWPORT_CAP_CLASS` (below, computed once at module scope)
               — a `max-[1180px]:w-[...]` Tailwind class, ONLY active under
               1180px viewport width. It sets
               `width: min(100%, calc((100vh - <budget>px) * 124/84))` so on a
               short/wide viewport (the client's iPad landscape complaint,
               1024x768) the calc() term wins once 100% width would make the
               aspect-ratio-derived height taller than the reserved budget —
               capping the pitch's OWN height so it, at least, never grows
               unbounded. `minWidth: 280px` (below) is the absolute floor.

          This class MUST be a static string literal (not built from a
          template referencing PITCH_WIDTH/PITCH_HEIGHT) — Tailwind's JIT scans
          raw source text for class candidates, not runtime values, so an
          interpolated class name would silently fail to generate any CSS in
          a production build. 124/84 is PITCH_WIDTH+4/PITCH_HEIGHT+4 with
          today's constants (120/80, `heatMapZones.ts`) — if those ever change,
          this literal needs a matching hand-edit.

          This does NOT by itself make the cockpit fit one iPad screen — the
          non-pitch chrome above it (~1215px measured at 1024x768, see
          team_findings.md 2026-08-14) is the dominant term and is out of this
          file's control. This only guarantees the pitch itself stops growing
          unbounded past a sane budget on a short/wide viewport, restoring the
          height-cap intent this file's comment used to (incorrectly) describe
          as already-implemented.
        */}
        <div className="relative overflow-visible">
          <div
            ref={fieldRef}
            data-testid="soccer-field"
            role="application"
            aria-label={`Tactical field: ${homeTeamName} vs ${awayTeamName}`}
            onClick={handleFieldClick}
            onPointerUp={handleFieldPointerUp}
            onPointerDownCapture={handleFieldPointerDownCapture}
            onPointerMove={handleFieldPointerMove}
            onPointerLeave={handleFieldPointerLeave}
            className={`w-full mx-auto bg-green-700 rounded-xl relative overflow-visible border-4 border-white shadow-inner touch-manipulation ${PITCH_VIEWPORT_CAP_CLASS}`}
            style={{
              /* Aspect ratio mirrors SoccerFieldHeatMap: (PITCH_WIDTH+4) / (PITCH_HEIGHT+4)
                 = 124 / 84, giving a 2-unit margin around the pitch markings.
                 Previously aspect-[105/68] which clipped the SVG + player chips. */
              aspectRatio: `${PITCH_WIDTH + 4} / ${PITCH_HEIGHT + 4}`,
              /* Width is intentionally NOT set here — see the comment above.
                 An inline `width` would always beat the responsive Tailwind
                 class below regardless of specificity, permanently defeating
                 the viewport cap. `w-full` in className is the base instead.
                 `mx-auto` (PMA-COCK-106) centers the leftover width symmetrically
                 once the cap binds and this div's computed width is narrower
                 than its (non-flex, full-width) parent — block-level centering
                 via margin:auto, independent of the surrounding flex context
                 higher up the tree. Live-verified: 209px right/21px left dead
                 space at 1024x768 before this change (client screenshot). */
              /* Container-query root (PMA-COCK-106): TacticalPlayerNode's jersey
                 circle/label sizing used `vw` (viewport width) clamp()s, which
                 stayed pinned to the full viewport even as this pitch div was
                 capped narrower than it by PITCH_VIEWPORT_CAP_CLASS — positions
                 are `%` of THIS div's own (narrower) box, so vw-sized content
                 didn't shrink to match, producing overlapping labels (18
                 overlapping pairs measured live at 1024x768, incl. the
                 client-reported cross-halfway-line case). Declaring this div a
                 size container lets descendants use `cqw` instead, coupling
                 their size to this box's ACTUAL rendered width at any cap
                 state. See TacticalPlayerNode.tsx for the consuming side.
                 Safari 16+ / Chrome 105+ — effectively all iPads in the field. */
              containerType: "inline-size",
              /* Realistic mowing-stripe pattern — 10 equal vertical bands,
                 alternating slightly lighter (base) and slightly darker overlays.
                 Combined with bg-green-700 (#15803d) gives a stadium-grass look. */
              minWidth: "280px",
              backgroundImage:
                "repeating-linear-gradient(90deg, rgba(0,0,0,0.07) 0%,rgba(0,0,0,0.07) 10%,transparent 10%,transparent 20%)",
            }}
          >
            {/* ─── Field Markings (IFAB-scaled, mapped to 120x80 coordinates) ─── */}
            {/* viewBox matches SoccerFieldHeatMap: -2 -2 124 84 gives a 2-unit
                margin around the 120x80 pitch so boundary lines are not flush
                with the container edge. preserveAspectRatio="xMidYMid meet"
                (same as heat map) keeps the pitch proportions correct. */}
            <svg
              className="absolute inset-0 w-full h-full pointer-events-none"
              viewBox={`-2 -2 ${PITCH_WIDTH + 4} ${PITCH_HEIGHT + 4}`}
              preserveAspectRatio="xMidYMid meet"
              data-testid="tactical-field-markings"
            >
              <g
                stroke="rgba(255,255,255,0.55)"
                strokeWidth={FIELD_LINE_STROKE_WIDTH}
                fill="none"
              >
                <rect
                  x={FIELD_BOUNDARY_INSET}
                  y={FIELD_BOUNDARY_INSET}
                  width={FIELD_BOUNDARY_WIDTH}
                  height={FIELD_BOUNDARY_HEIGHT}
                  rx={0.5}
                  data-testid="tactical-field-boundary"
                />
                <line
                  x1={FIELD_BOUNDARY_INSET}
                  y1={FIELD_BOTTOM_LINE_Y}
                  x2={PITCH_WIDTH - FIELD_BOUNDARY_INSET}
                  y2={FIELD_BOTTOM_LINE_Y}
                  strokeLinecap="square"
                  data-testid="tactical-bottom-sideline"
                />
                <line
                  x1={PITCH_WIDTH / 2}
                  y1={0}
                  x2={PITCH_WIDTH / 2}
                  y2={PITCH_HEIGHT}
                />
                <ellipse
                  cx={PITCH_WIDTH / 2}
                  cy={PITCH_HEIGHT / 2}
                  rx={CC_RX}
                  ry={CC_RY}
                  data-testid="tactical-center-circle"
                />
                <circle
                  cx={PITCH_WIDTH / 2}
                  cy={PITCH_HEIGHT / 2}
                  r={PEN_R}
                  fill="rgba(255,255,255,0.55)"
                />
                <rect
                  x={0}
                  y={PA_Y}
                  width={PA_WIDTH}
                  height={PA_HEIGHT}
                  data-testid="tactical-penalty-left"
                />
                <rect
                  x={PITCH_WIDTH - PA_WIDTH}
                  y={PA_Y}
                  width={PA_WIDTH}
                  height={PA_HEIGHT}
                  data-testid="tactical-penalty-right"
                />
                <rect
                  x={0}
                  y={GA_Y}
                  width={GA_WIDTH}
                  height={GA_HEIGHT}
                  data-testid="tactical-goalarea-left"
                />
                <rect
                  x={PITCH_WIDTH - GA_WIDTH}
                  y={GA_Y}
                  width={GA_WIDTH}
                  height={GA_HEIGHT}
                  data-testid="tactical-goalarea-right"
                />
                {/* EPIC-96 (T4): the non-interactive "developed indicator"
                    the client asked for in place of the retired chip — a
                    subtle cyan tint + outline over the ATTACKED six-yard
                    box, the same box the two rects above always draw.
                    Explicit fill/stroke/pointerEvents override this
                    group's own `fill="none"` — this element's own visual
                    identity is independent of the plain field-line group
                    it happens to sit inside. */}
                {shotGuideEdge && (
                  <rect
                    x={shotGuideEdge === "left" ? 0 : PITCH_WIDTH - GA_WIDTH}
                    y={GA_Y}
                    width={GA_WIDTH}
                    height={GA_HEIGHT}
                    data-testid="tactical-shot-guide"
                    data-tactical-guide-edge={shotGuideEdge}
                    fill="rgba(34, 211, 238, 0.12)"
                    stroke="rgba(34, 211, 238, 0.55)"
                    strokeWidth={FIELD_LINE_STROKE_WIDTH}
                    pointerEvents="none"
                  />
                )}
                <circle
                  cx={PEN_X}
                  cy={PITCH_HEIGHT / 2}
                  r={PEN_R}
                  fill="rgba(255,255,255,0.55)"
                />
                <circle
                  cx={PITCH_WIDTH - PEN_X}
                  cy={PITCH_HEIGHT / 2}
                  r={PEN_R}
                  fill="rgba(255,255,255,0.55)"
                />
                <path
                  d={`M ${PA_WIDTH} ${
                    PITCH_HEIGHT / 2 - PENALTY_ARC_Y_OFFSET
                  } A ${CC_RX} ${CC_RY} 0 0 1 ${PA_WIDTH} ${
                    PITCH_HEIGHT / 2 + PENALTY_ARC_Y_OFFSET
                  }`}
                />
                <path
                  d={`M ${PITCH_WIDTH - PA_WIDTH} ${
                    PITCH_HEIGHT / 2 - PENALTY_ARC_Y_OFFSET
                  } A ${CC_RX} ${CC_RY} 0 0 0 ${PITCH_WIDTH - PA_WIDTH} ${
                    PITCH_HEIGHT / 2 + PENALTY_ARC_Y_OFFSET
                  }`}
                />
                <path
                  d={`M 0 ${CORNER_R} A ${CORNER_R} ${CORNER_R} 0 0 1 ${CORNER_R} 0`}
                />
                <path
                  d={`M ${
                    PITCH_WIDTH - CORNER_R
                  } 0 A ${CORNER_R} ${CORNER_R} 0 0 1 ${PITCH_WIDTH} ${CORNER_R}`}
                />
                <path
                  d={`M 0 ${
                    PITCH_HEIGHT - CORNER_R
                  } A ${CORNER_R} ${CORNER_R} 0 0 0 ${CORNER_R} ${PITCH_HEIGHT}`}
                />
                <path
                  d={`M ${
                    PITCH_WIDTH - CORNER_R
                  } ${PITCH_HEIGHT} A ${CORNER_R} ${CORNER_R} 0 0 0 ${PITCH_WIDTH} ${
                    PITCH_HEIGHT - CORNER_R
                  }`}
                />
              </g>
            </svg>

            {/* ─── Drag bounds overlay (hidden during live – bounds are full field) ─── */}
            {draggingPlayerId &&
              !isMatchLive &&
              (() => {
                // Find the dragged player's position and side
                const allPlayers = [
                  ...homePlayers.map((p) => ({ ...p, side: "home" as const })),
                  ...awayPlayers.map((p) => ({ ...p, side: "away" as const })),
                ];
                const draggedPlayer = allPlayers.find(
                  (p) => p.id === draggingPlayerId,
                );
                if (!draggedPlayer) return null;
                const bounds = getBoundsForPlayer(
                  draggedPlayer.position,
                  draggedPlayer.side,
                  isMatchLive,
                );
                // Convert bounds to display space (may need flip). EPIC-97:
                // 180-degree rotation — both axes invert and their min/max
                // swap accordingly.
                const displayBounds = flipSides
                  ? {
                      xMin: 100 - bounds.xMax,
                      xMax: 100 - bounds.xMin,
                      yMin: 100 - bounds.yMax,
                      yMax: 100 - bounds.yMin,
                    }
                  : bounds;
                const group = getPositionGroup(draggedPlayer.position);
                const colorMap: Record<string, string> = {
                  goalkeeper: "rgba(255, 220, 0, 0.15)",
                  defense: "rgba(59, 130, 246, 0.12)",
                  midfield: "rgba(34, 197, 94, 0.12)",
                  attack: "rgba(239, 68, 68, 0.12)",
                  other: "rgba(255, 255, 255, 0.08)",
                };
                const borderColorMap: Record<string, string> = {
                  goalkeeper: "rgba(255, 220, 0, 0.6)",
                  defense: "rgba(59, 130, 246, 0.5)",
                  midfield: "rgba(34, 197, 94, 0.5)",
                  attack: "rgba(239, 68, 68, 0.5)",
                  other: "rgba(255, 255, 255, 0.3)",
                };
                return (
                  <div
                    data-testid="drag-bounds-overlay"
                    className="absolute pointer-events-none z-[5] transition-opacity duration-150"
                    style={{
                      left: `${displayBounds.xMin}%`,
                      top: `${displayBounds.yMin}%`,
                      width: `${displayBounds.xMax - displayBounds.xMin}%`,
                      height: `${displayBounds.yMax - displayBounds.yMin}%`,
                      backgroundColor: colorMap[group] ?? colorMap.other,
                      border: `2px dashed ${
                        borderColorMap[group] ?? borderColorMap.other
                      }`,
                      borderRadius: "8px",
                    }}
                  />
                );
              })()}

            {/* ─── Collision proximity rings (shown during drag) ─── */}
            {draggingPlayerId &&
              allPositions &&
              (() => {
                const rings: React.ReactNode[] = [];
                for (const [pid, pos] of allPositions) {
                  if (pid === draggingPlayerId) continue;
                  // EPIC-97: 180-degree rotation — both axes invert.
                  const display = flipSides
                    ? { x: 100 - pos.x, y: 100 - pos.y }
                    : pos;
                  rings.push(
                    <div
                      key={`ring-${pid}`}
                      className="absolute pointer-events-none z-[4] rounded-full border border-white/20"
                      style={{
                        left: `${display.x}%`,
                        top: `${display.y}%`,
                        width: `${MIN_PLAYER_SEPARATION * 2}%`,
                        height: `${MIN_PLAYER_SEPARATION * 2}%`,
                        transform: "translate(-50%, -50%)",
                        backgroundColor: "rgba(255, 255, 255, 0.04)",
                      }}
                    />,
                  );
                }
                return rings;
              })()}

            {/* ─── Player Nodes ─── */}
            {/*
              The player layer is inset to the INNER pitch region (matching the
              SVG's 2-unit viewBox margin) so that xPercent/yPercent=0..100
              maps onto the actual pitch boundary lines rather than the outer
              container edge.  pitchRef is used for click-to-place coordinate
              conversion so render position and destination click share the same
              reference frame.
            */}
            <div
              ref={pitchRef}
              data-testid="tactical-field-pitch-region"
              className="absolute overflow-visible pointer-events-none"
              style={{
                left: `${PITCH_LEFT_PCT}%`,
                top: `${PITCH_TOP_PCT}%`,
                width: `${PITCH_W_PCT}%`,
                height: `${PITCH_H_PCT}%`,
              }}
            >
              {homePlayers
                .filter((p) => !visiblePlayerIds || visiblePlayerIds.has(p.id))
                .map((p) => renderPlayerNode(p, "home"))}
              {awayPlayers
                .filter((p) => !visiblePlayerIds || visiblePlayerIds.has(p.id))
                .map((p) => renderPlayerNode(p, "away"))}

              {/* ─── PMA-COCK-153: hover tooltip, driven by the SAME nearest-
                  token resolver the pointerdown redirect uses (not the
                  removed native `title`, which was resolved by raw DOM
                  stacking order — the exact bug this UC closes). Anchored to
                  the RESOLVED token's own position, not the raw cursor, so it
                  unambiguously labels which player a click here would hit.
                  pointer-events-none so it can never itself become a hit
                  target. */}
              {hoverPlayerId &&
                (() => {
                  const hovered = renderedPlayers.find(
                    (p) => p.id === hoverPlayerId,
                  );
                  if (!hovered) return null;
                  const display = getTokenDisplay(hovered.id);
                  return (
                    <div
                      data-testid="tactical-hover-tooltip"
                      data-hover-player-id={hovered.id}
                      className="absolute z-20 pointer-events-none px-2 py-1 rounded-md bg-slate-900/95 text-white text-xs font-semibold shadow-lg whitespace-nowrap"
                      style={{
                        left: `${display.x}%`,
                        top: `${display.y}%`,
                        transform: "translate(-50%, -160%)",
                      }}
                    >
                      {formatDisplayName(hovered.full_name)} ({hovered.position}
                      )
                    </div>
                  );
                })()}

              {/* EPIC-96 (AC3, client wave 2026-09-24): the PMA-COCK-181
                  on-target chip that used to render here, over the
                  player-token layer, is retired — it sat
                  `z-20 pointer-events-auto` above every token, which is
                  exactly what let it intercept a tap meant for the
                  goalkeeper ("me estorba al querer seleccionar al
                  portero"). The equivalent action is now a labelled button
                  in `ActionStage.tsx`'s own hint bar, off the pitch
                  entirely (`field-ontarget-btn`, calling this file's
                  exported `buildOnTargetChipCoordinate` directly) — nothing
                  replaces the chip on the pitch itself, so every token,
                  including the keeper standing on the goal mouth, stays
                  fully tappable during shot destination. */}

              {/* EPIC-96 (T4): the tap-feedback ripple — purely decorative
                  confirmation of where an open-pitch tap landed, gone again
                  within TAP_RIPPLE_DURATION_MS. `key={tapRipple.rippleId}`
                  forces a fresh mount (and therefore a fresh CSS
                  transition) even when two taps land at the exact same
                  point in a row. pointer-events-none so it can never itself
                  become a hit target, on top (no explicit z-index needed —
                  it mounts after every token in this same stacking
                  context) so the confirmation is actually visible even
                  over a token. */}
              {tapRipple && (
                <div
                  key={tapRipple.rippleId}
                  data-testid="tactical-tap-ripple"
                  className="absolute pointer-events-none rounded-full border-2 border-cyan-300"
                  style={{
                    left: `${tapRipple.x}%`,
                    top: `${tapRipple.y}%`,
                    width: "2.4rem",
                    height: "2.4rem",
                    // No base `transform` here — the keyframes' own `from`
                    // step (index.css's tactical-tap-ripple) already
                    // includes the same translate(-50%, -50%) centering,
                    // and owns the property for this element's entire
                    // lifetime from mount.
                    animation: `tactical-tap-ripple ${TAP_RIPPLE_DURATION_MS}ms ease-out forwards`,
                  }}
                />
              )}
            </div>

            {/* ─── Overlay (e.g. QuickActionMenu) ─── */}
            {overlay && (
              <div
                className={`absolute inset-0 z-30 ${
                  showDestinationControls
                    ? "pointer-events-none"
                    : "pointer-events-auto"
                }`}
                onClick={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
              >
                <div className="relative h-full w-full">{overlay}</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TacticalField;
